import { ColumnsType } from "antd/lib/table";
import moment from "moment";
import { fixNumber, formatMoney } from "src/utils/number";
import { CancelCircleIcon } from "@hugeicons/core-free-icons";
import HugeIcon from "src/components/HugeIcon";
import styled from "styled-components";
import { Badge as UIBadge, Button as UIButton } from "src/components/ui";
import { orderStatus, orderType } from "./constants";
import { colors } from "src/theme/colors";

type OrdersColumnsFn = (
  onCancelOrder: { (record: any, id: number): void },
  cancelOrderLoading: boolean,
  selectedId: number,
) => ColumnsType<any>;

/**
 * A date and a time, in one order.
 *
 * Every date on this table was formatted "MMM DD YYYY HH:mm A z", which put
 * a 24-hour clock next to a meridiem and a timezone token that resolves to
 * nothing — so an order placed at a quarter past three in the afternoon was
 * printed "Jul 31 2026 15:05 PM". Either notation is fine; both at once is
 * not, and "15:05 PM" is not a time.
 */
export const orderDateTime = (date: any) =>
  date ? moment(date).format("DD MMM YYYY, HH:mm") : "—";

/* One dot per state, in the product's status tones rather than a set of
   hand-picked hex values with a #007bff and a #6c757d in it. */
const statusTone: Record<string, "success" | "warning" | "error" | "gray"> = {
  [orderStatus.EXECUTED]: "success",
  [orderStatus.FILLED]: "success",
  [orderStatus.EXECUTING]: "warning",
  [orderStatus.PENDING]: "warning",
  [orderStatus.PRE_PENDING]: "warning",
  [orderStatus.CANCELING]: "warning",
  [orderStatus.REJECTED]: "error",
  [orderStatus.FAILED]: "error",
  [orderStatus.CANCELED]: "error",
  [orderStatus.EXPIRED]: "gray",
};

/**
 * Which way the order goes.
 *
 * It was blue for BUY and ORANGE for SELL — two colours from no palette, and
 * an orange that reads as a warning. A side is not a direction of PRICE, so
 * it must not borrow the green and red this product keeps for a gain and a
 * loss; it is a fact about the order, so it is set as a fact: the word, in
 * ink, with the weight doing the separating.
 */
/* The figure the row is about, so a column of them can be scanned. */
const Emphasis = styled.span`
  font-weight: 600;
  color: ${colors.gray[900]};
`;

const Side = styled.span`
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.03em;
  color: ${colors.gray[900]};
`;

export const orderStatusColors = {
  EXECUTED: colors.success[600], // Green
  EXECUTING: "#ffc107", // Yellow
  PRE_PENDING: "#007bff", // Blue
  PENDING: "#007bff", // Blue
  REJECTED: colors.error[600], // Red
  EXPIRED: "#6c757d", // Gray
  FAILED: colors.error[600], // Red
  CANCELING: "#ffc107", // Yellow
  CANCELED: colors.error[600], // Red
  FILLED: colors.success[600], // Green
};

export const orderSideColors = {
  BUY: "#007bff", // Blue
  SELL: "#F5B911", // Orange
};

export const orderTypeColors = {
  MARKET: "blue", // Blue
  LIMIT: "magenta", // Pink
};

export const ordersColumns: OrdersColumnsFn = (
  onCancelOrder: { (record: any, id: number): void },
  cancelOrderLoading,
  selectedId: number,
) => {
  /**
   * The relevant columns, not every column.
   *
   * There were THIRTEEN, and they needed 2300px of horizontal scroll to show
   * — so the table had a frozen action column permanently covering the last
   * figure, and a reader had to scroll sideways inside a card to find out
   * what an order cost. Six of the thirteen were the same three figures twice
   * over: the price, value and quantity as ORDERED, then again as FILLED.
   *
   * An order that filled is described by its fill; one that has not filled
   * yet is described by what was asked. So each row now shows whichever of
   * the two pair is true for THAT row, and says which in the cell's title.
   * Time-in-force came off as well: it is a property of the instruction, not
   * of what happened, and it is set in the dialog that places the order.
   */
  const filled = (record: any) =>
    [orderStatus.FILLED, orderStatus.EXECUTED].includes(record?.status);

  const columns: ColumnsType<any> = [
    {
      title: "Order",
      width: 90,
      dataIndex: "order_side",
      key: "order_side",
      filters: [
        { text: "BUY", value: "BUY" },
        { text: "SELL", value: "SELL" },
      ],
      onFilter: (value: string, record) =>
        record.order_side.indexOf(value) === 0,
      render: (order_side) => <Side>{order_side}</Side>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 140,
      filters: [
        { text: "Executed", value: orderStatus.EXECUTED },
        { text: "Executing", value: orderStatus.EXECUTING },
        { text: "Pending", value: orderStatus.PENDING },
        { text: "Rejected", value: orderStatus.REJECTED },
        { text: "Expired", value: orderStatus.EXPIRED },
        { text: "Canceled", value: orderStatus.CANCELED },
        { text: "Filled", value: orderStatus.FILLED },
      ],
      onFilter: (value: string, record) => record.status.indexOf(value) === 0,
      render: (status) => (
        <UIBadge tone={statusTone[status] ?? "gray"} dot>
          {status}
        </UIBadge>
      ),
    },
    {
      title: "Type",
      dataIndex: "order_type",
      key: "order_type",
      width: 110,
      filters: [
        { text: "Market", value: orderType.Market },
        { text: "Limit", value: orderType.Limit },
      ],
      onFilter: (value: string, record) =>
        record.order_type.indexOf(value) === 0,
      render: (order_type) => <UIBadge tone="gray">{order_type}</UIBadge>,
    },
    {
      title: "Placed",
      dataIndex: "created_at",
      key: "created_at",
      render: orderDateTime,
      sorter: (a, b) => (moment(a.created_at).isBefore(b.created_at) ? -1 : 1),
    },
    {
      title: "Executed",
      dataIndex: "updated_at",
      key: "updated_at",
      render: orderDateTime,
      sorter: (a, b) => (moment(a.updated_at).isBefore(b.updated_at) ? -1 : 1),
    },
    {
      title: "Quantity",
      dataIndex: "order_quantity",
      key: "order_quantity",
      align: "right",
      width: 110,
      sorter: (a, b) => a.order_quantity - b.order_quantity,
      /* "1 of 2" when only part of the order went through — a partial fill
         used to be readable only by comparing two columns that were nine
         columns apart. */
      render: (quantity, record) =>
        record?.fill_quantity != null &&
        Number(record.fill_quantity) !== Number(quantity) ? (
          <span title={`${record.fill_quantity} filled of ${quantity} ordered`}>
            {record.fill_quantity} of {quantity}
          </span>
        ) : (
          quantity
        ),
    },
    {
      title: "Price",
      key: "price",
      align: "right",
      width: 120,
      render: (_, record) => {
        const isFilled = filled(record);
        const price = isFilled ? record?.fill_price : record?.order_price;
        return (
          <span title={isFilled ? "Fill price" : "Order price"}>
            ${formatMoney(fixNumber(price, 3))}
          </span>
        );
      },
    },
    {
      title: "Value",
      key: "value",
      align: "right",
      width: 130,
      render: (_, record) => {
        const isFilled = filled(record);
        const value = isFilled ? record?.fill_value : record?.order_value;
        return (
          <Emphasis title={isFilled ? "Fill value" : "Order value"}>
            ${formatMoney(fixNumber(value, 3))}
          </Emphasis>
        );
      },
    },
    {
      title: "Actions",
      width: 110,
      key: "operation",
      align: "right",
      render: (record, _) => (
        <UIButton
          variant="destructive"
          size="xs"
          loading={cancelOrderLoading && selectedId === record.id}
          onClick={() => onCancelOrder(record, record.id)}
          disabled={
            ![
              orderStatus.PENDING,
              orderStatus.PRE_PENDING,
              orderStatus.EXECUTING,
            ].includes(record.status)
          }
        >
          Cancel
        </UIButton>
      ),
    },
  ];

  return columns;
};
